import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  FrameGridPlacement,
  VirtualViewportItemContext,
  VirtualViewportTrackBy,
} from '../virtualization.types';

interface FrameGridItem<T> {
  index: number;
  key: string;
  item: T;
  gridColumn: string;
  gridRow: string;
}

@Component({
  selector: 'app-frame-grid-layout',
  templateUrl: './frame-grid-layout.component.html',
  styleUrls: ['./frame-grid-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FrameGridLayoutComponent<T> implements AfterViewInit, OnChanges, OnDestroy {
  @Input() items: T[] = [];
  @Input() trackBy: VirtualViewportTrackBy<T> = (index: number) => index;
  @Input() appendDisabled = false;
  @Input() appendTriggerMarginPx = 1200;
  @Input() frame: number[][] = [[1, 2, 3], [4, 5, 6]];
  @Input() gapPx = 4;
  @Input() itemTemplate?: TemplateRef<VirtualViewportItemContext<T>>;

  @Output() appendRequested = new EventEmitter<void>();
  @Output() layoutChange = new EventEmitter<void>();

  @ContentChild(TemplateRef) projectedItemTemplate?: TemplateRef<VirtualViewportItemContext<T>>;
  @ViewChild('layoutRoot', { static: true }) layoutRoot!: ElementRef<HTMLElement>;
  @ViewChild('appendSentinel', { static: true }) appendSentinel!: ElementRef<HTMLElement>;

  public layoutItems: FrameGridItem<T>[] = [];
  public resolvedColumns = 1;
  public gridAutoRowsPx = 120;

  private readonly isBrowser: boolean;
  private placements: FrameGridPlacement[] = [];
  private patternRowCount = 1;
  private appendObserver: IntersectionObserver | null = null;
  private containerResizeObserver: ResizeObserver | null = null;
  private lastAppendTriggerLength = -1;
  private lastAppendDisabled = false;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  get resolvedTemplate(): TemplateRef<VirtualViewportItemContext<T>> | null {
    return this.itemTemplate || this.projectedItemTemplate || null;
  }

  get gridTemplateColumns(): string {
    return `repeat(${this.resolvedColumns}, minmax(0, 1fr))`;
  }

  ngAfterViewInit(): void {
    this.observeContainer();
    this.scheduleLayoutRebuild();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['appendDisabled'] && this.lastAppendDisabled && !this.appendDisabled) {
      this.lastAppendTriggerLength = -1;
    }
    this.lastAppendDisabled = this.appendDisabled;

    if (changes['items'] || changes['frame'] || changes['gapPx']) {
      this.scheduleLayoutRebuild();
    }
  }

  ngOnDestroy(): void {
    this.appendObserver?.disconnect();
    this.containerResizeObserver?.disconnect();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.scheduleLayoutRebuild();
  }

  trackLayoutItem(_: number, entry: FrameGridItem<T>): string {
    return entry.key;
  }

  private observeContainer(): void {
    if (!this.isBrowser || typeof ResizeObserver === 'undefined') {
      return;
    }

    this.containerResizeObserver = new ResizeObserver(() => this.scheduleLayoutRebuild());
    this.containerResizeObserver.observe(this.layoutRoot.nativeElement);
  }

  private scheduleLayoutRebuild(): void {
    const schedule = this.isBrowser && typeof requestAnimationFrame === 'function'
      ? requestAnimationFrame
      : (cb: FrameRequestCallback) => setTimeout(() => cb(Date.now()), 0);

    schedule(() => {
      this.buildPlacements();
      this.buildLayoutItems();
      this.syncAppendObserver();
      this.maybeRequestAppendFromProximity();
      this.cdr.markForCheck();
      this.layoutChange.emit();
    });
  }

  private buildPlacements(): void {
    const normalizedFrame = Array.isArray(this.frame) && this.frame.length ? this.frame : [[1]];
    const columns = Math.max(1, normalizedFrame.reduce((max, row) => Math.max(max, row?.length || 0), 1));
    const positions = new Map<number, { firstIndex: number; minRow: number; maxRow: number; minColumn: number; maxColumn: number }>();

    normalizedFrame.forEach((row, rowIndex) => {
      row.forEach((value, columnIndex) => {
        const current = positions.get(value);
        if (!current) {
          positions.set(value, {
            firstIndex: (rowIndex * columns) + columnIndex,
            minRow: rowIndex,
            maxRow: rowIndex,
            minColumn: columnIndex,
            maxColumn: columnIndex,
          });
          return;
        }

        current.minRow = Math.min(current.minRow, rowIndex);
        current.maxRow = Math.max(current.maxRow, rowIndex);
        current.minColumn = Math.min(current.minColumn, columnIndex);
        current.maxColumn = Math.max(current.maxColumn, columnIndex);
      });
    });

    this.resolvedColumns = columns;
    this.patternRowCount = Math.max(1, normalizedFrame.length);
    this.placements = Array.from(positions.entries())
      .sort(([, a], [, b]) => a.firstIndex - b.firstIndex)
      .map(([value, placement]) => ({
        key: `${value}`,
        rowStart: placement.minRow + 1,
        columnStart: placement.minColumn + 1,
        rowSpan: placement.maxRow - placement.minRow + 1,
        columnSpan: placement.maxColumn - placement.minColumn + 1,
      }));

    this.gridAutoRowsPx = this.resolveCellSizePx();
  }

  private buildLayoutItems(): void {
    if (!this.placements.length) {
      this.layoutItems = [];
      return;
    }

    this.layoutItems = this.items.map((item, index) => {
      const placement = this.placements[index % this.placements.length];
      const groupIndex = Math.floor(index / this.placements.length);
      const rowStart = placement.rowStart + (groupIndex * this.patternRowCount);

      return {
        index,
        key: this.resolveKey(item, index),
        item,
        gridColumn: `${placement.columnStart} / span ${placement.columnSpan}`,
        gridRow: `${rowStart} / span ${placement.rowSpan}`,
      };
    });
  }

  private resolveCellSizePx(): number {
    const hostWidth = this.layoutRoot?.nativeElement?.clientWidth
      || this.document.documentElement?.clientWidth
      || (this.isBrowser ? window.innerWidth : 0)
      || 0;
    const totalGap = Math.max(0, (this.resolvedColumns - 1) * this.gapPx);
    const availableWidth = Math.max(0, hostWidth - totalGap);
    const size = this.resolvedColumns > 0 ? availableWidth / this.resolvedColumns : 0;

    return Math.max(24, Math.floor(size || 120));
  }

  private syncAppendObserver(): void {
    if (!this.isBrowser || typeof IntersectionObserver === 'undefined') {
      return;
    }

    if (!this.appendObserver) {
      this.appendObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }
        this.maybeRequestAppendFromProximity();
      }, {
        root: null,
        rootMargin: `${this.appendTriggerMarginPx}px 0px ${this.appendTriggerMarginPx}px 0px`,
        threshold: 0,
      });
    }

    this.appendObserver.observe(this.appendSentinel.nativeElement);
  }

  private maybeRequestAppendFromProximity(): void {
    if (!this.isBrowser || this.appendDisabled || !this.items.length) {
      return;
    }

    const rect = this.appendSentinel.nativeElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight || this.document.documentElement?.clientHeight || 0;
    const withinAppendZone = rect.top <= viewportHeight + this.appendTriggerMarginPx
      && rect.bottom >= -this.appendTriggerMarginPx;

    if (!withinAppendZone || this.lastAppendTriggerLength === this.items.length) {
      return;
    }

    this.lastAppendTriggerLength = this.items.length;
    this.appendRequested.emit();
  }

  private resolveKey(item: T, index: number): string {
    const key = this.trackBy(index, item);
    if (typeof key === 'string' || typeof key === 'number') {
      return `${key}`;
    }

    return `index:${index}`;
  }
}
